import * as React from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useNavigate } from 'react-router'

import { DefaultErrorBoundary } from '../../main/components/error-view'
import { Paragraph } from '../../main/components/typography'
import CryptoMaps from '../crypto/maps'
import * as tracking from '../shared/tracking'
import { Map } from './components/Map'
import { MapSelect } from './components/MapSelect'
import { SearchInput, SearchResult, useSearch } from './components/Search'
import { Topbar } from './components/Topbar'
import { Expand } from './components/Topbar/Expand'
import { Share } from './components/Topbar/Share'
import { Zoom } from './components/Topbar/Zoom'
import { MAP_SUBTYPES_CRYPTO } from './constants/constants'
import { useMapData } from './hooks/use-map-data'
import { useMapType } from './hooks/use-map-type'
import * as api from './services/api'
import { MapDataRoot, MapSubtypeId, MapTypeId } from './types'
import * as mapUtils from './utils'

export interface MapInitProps {
  initData?: MapDataRoot
  cacheBuster: number
  generate?: boolean
}

function getMapTypeIdFromCurrency(currency: string | null) {
  switch (currency?.toUpperCase()) {
    case 'USDT':
      return MapTypeId.CryptoUSDT
    case 'EUR':
      return MapTypeId.CryptoEUR
    case 'BTC':
      return MapTypeId.CryptoBTC
    default:
      return MapTypeId.CryptoUSD
  }
}

export function CryptoMapApp(props: MapInitProps) {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const search = useSearch()
  const navigate = useNavigate()
  const { type, subtype } = useMapType((query) => getMapTypeIdFromCurrency(query.get('c')))
  const mapData = useMapData(type.type, subtype.value, props.initData, undefined, () =>
    api.mapPerf(type.type, subtype.value)
  )

  /**
   * Set new subtype to be false on perf data load so we don’t display loading state when fetching
   * new perf data for the same type
   */
  const [isNewDataType, setIsNewDataType] = React.useState(false)
  React.useEffect(() => {
    if (!mapData.perfDataQuery.isFetching) {
      setIsNewDataType(false)
    }
  }, [mapData.perfDataQuery.isFetching])

  const isLoadingInitialBaseData = !mapData.baseDataQuery.isPreviousData && mapData.baseDataQuery.isLoading
  const isLoadingInitialPerf = !mapData.perfDataQuery.isPreviousData && mapData.perfDataQuery.isLoading
  const isInitialLoading = isLoadingInitialBaseData || isLoadingInitialPerf
  const isFetchingNewPerf = isNewDataType && !mapData.perfDataQuery.isLoading && mapData.perfDataQuery.isFetching

  return (
    <>
      {!isExpanded && <CryptoMaps />}
      <Topbar
        className="border border-[#d3d3d3] bg-[#eee] text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
        contentClass="-ml-[2px]"
        mapControls={
          <>
            <div className="flex px-2">
              <Expand className="text-link" data={mapData.data} onExpand={setIsExpanded} />
              <Share className="text-link" mapType={type} subtype={subtype} />
            </div>
            <Zoom />
          </>
        }
      >
        <Paragraph size="small" className="leading-tight">
          {type.description}
        </Paragraph>
      </Topbar>
      <div className="flex h-full">
        <div className="map-sidebar mt-0.5 w-50">
          <div className="border border-[#d3d3d3] bg-[#eee] p-4 dark:border-gray-600 dark:bg-gray-700">
            <MapSelect
              label={<span className="m-0 text-3xs uppercase text-gray-500 dark:text-gray-300">Data type</span>}
              aria-label="Data type"
              theme={FinvizSettings.hasDarkTheme ? 'dark' : 'light'}
              value={subtype.value}
              items={MAP_SUBTYPES_CRYPTO}
              isLoading={isFetchingNewPerf}
              onChange={(item) => {
                const query = new URLSearchParams(window.location.search)
                if (item.value === MapSubtypeId.DayPerf) {
                  query.delete('st')
                } else {
                  query.set('st', item.value)
                }

                setIsNewDataType(true)
                const stringQuery = query.toString()
                void navigate({ pathname: window.location.pathname, search: stringQuery }, { replace: true })
                tracking.trackPageView({ query: stringQuery })
                document.title = `Crypto Maps - ${item.label}`
              }}
            />

            <div className="mt-4">
              <SearchInput value={search.value} onChange={search.onInputChange} theme="light" />
              <ul className="mt-0.5 divide-y divide-[#d3d3d3] py-2 dark:divide-gray-600">
                {search.results.map((result) => (
                  <SearchResult
                    key={result.name}
                    result={result}
                    onClick={search.onItemClick}
                    onMouseLeave={search.onResultMouseLeave}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-50"
                  />
                ))}
              </ul>
            </div>
          </div>
        </div>

        <ErrorBoundary resetKeys={[type.type, subtype.value]} FallbackComponent={DefaultErrorBoundary}>
          <Map
            className="mb-3 min-h-145 pl-1 pt-0.5"
            isFetchingPerf={isFetchingNewPerf}
            isLoadingData={isInitialLoading}
            data={mapData.data}
            type={type}
            subtype={subtype}
            onNodeClick={mapUtils.onNodeClick}
            dataHash={mapData.perfDataQuery.data?.hash}
          />
        </ErrorBoundary>
      </div>
    </>
  )
}
